import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { PublicShell } from "@/pages/public/PublicQuote";
import { Phone, Key, ArrowRight, MessageCircle } from "lucide-react";
import InstallAppButton from "@/components/InstallAppButton";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ClientLogin() {
  const [phone, setPhone] = useState("");
  const [otp, setOtp] = useState("");
  const [step, setStep] = useState(1);
  const [staffUrl, setStaffUrl] = useState("");
  const [otpDemo, setOtpDemo] = useState("");
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const requestOtp = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/client-auth/request-otp`, { phone });
      setStaffUrl(data.staff_send_url);
      setOtpDemo(data.otp_demo);
      // Auto-fill the OTP so the customer flows through automatically
      if (data.otp_demo) setOtp(data.otp_demo);
      setStep(2);
      toast.success(`Welcome ${data.customer_name} — signing you in…`);
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    setBusy(false);
  };

  const verify = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/client-auth/verify-otp`, { phone, otp });
      localStorage.setItem("client_token", data.token);
      localStorage.setItem("client_name", data.customer.name);
      nav("/portal");
    } catch (e) { toast.error(e.response?.data?.detail || "Invalid OTP"); }
    setBusy(false);
  };

  // Auto-submit once we're on step 2 with a filled OTP — instant login
  useEffect(() => {
    if (step === 2 && otp && otp.length === 6 && !busy) {
      const t = setTimeout(() => verify(), 600);
      return () => clearTimeout(t);
    }
    // eslint-disable-next-line
  }, [step, otp]);

  return (
    <PublicShell>
      <div className="max-w-md mx-auto">
        <div className="label-tiny mb-1">Client Portal</div>
        <h1 className="font-display text-3xl tracking-tight mb-2">Welcome back</h1>
        <p className="text-sm text-[#757169] mb-6">See your orders, files and invoices anytime.</p>

        {step === 1 && (
          <form onSubmit={requestOtp} className="card-surface p-6 space-y-4" data-testid="client-otp-request">
            <div>
              <div className="label-tiny mb-1.5">Your phone number</div>
              <div className="relative">
                <Phone size={14} className="absolute left-3 top-3 text-[#757169]"/>
                <input required autoFocus className="input-field pl-9" placeholder="10-digit mobile"
                  value={phone} onChange={e=>setPhone(e.target.value)} data-testid="client-phone"/>
              </div>
            </div>
            <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="client-request-otp">
              Get OTP <ArrowRight size={14}/>
            </button>
            <p className="text-[11px] text-[#757169] text-center">Our team will send a 6-digit code to your WhatsApp.</p>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={verify} className="card-surface p-6 space-y-4" data-testid="client-otp-verify">
            <div>
              <div className="label-tiny mb-1.5">Enter the 6-digit OTP</div>
              <div className="relative">
                <Key size={14} className="absolute left-3 top-3 text-[#757169]"/>
                <input required autoFocus maxLength={6} className="input-field pl-9 mono text-center text-lg tracking-widest"
                  placeholder="••••••" value={otp} onChange={e=>setOtp(e.target.value.replace(/\D/g,""))} data-testid="client-otp-input"/>
              </div>
            </div>
            {staffUrl && (
              <a href={staffUrl} target="_blank" rel="noreferrer"
                className="block bg-[#25D366] hover:bg-[#1EBE5A] text-white text-sm text-center py-2.5 rounded-full font-medium flex items-center justify-center gap-2 no-underline">
                <MessageCircle size={14}/> Staff: send OTP on WhatsApp
              </a>
            )}
            {otpDemo && (
              <div className="text-[11px] text-center bg-[#DDEADE] text-[#2E4A38] p-2 rounded flex items-center justify-center gap-1.5">
                <Key size={11}/> Auto-signing in with OTP <b className="mono">{otpDemo}</b>…
              </div>
            )}
            <button type="submit" disabled={busy} className="btn-primary w-full">{busy ? "Signing in…" : "Sign in"}</button>
            <button type="button" onClick={()=>setStep(1)} className="btn-ghost w-full text-xs">Change phone</button>
          </form>
        )}

        <div className="text-center mt-6 space-y-3">
          <InstallAppButton variant="ghost" />
          <a href="/login" className="block text-xs text-[#757169] hover:underline">Staff sign-in →</a>
        </div>
      </div>
    </PublicShell>
  );
}
